import * as React from "react";
import { useMemo } from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import LazyRetinaImage from "~components/shared/lazy-retina-image/LazyRetinaImage";
import PlayIconWithDuration from "~components/shared/play-icon-with-duration/PlayIconWithDuration";
import type {
    CuratedItem as HydratedCuratedItem,
    Image as HydratedImage,
} from "~types/types";

import { MediaFormat, type MediaChildProps } from "../";
import styles from "../Media.module.css";
import { isImageMediaFormat } from "../utils/internal";
import type { HydratedAuthorImageData, MediaConfig } from "../utils/types";

/**
 * Returns appropriate Image for valid Image MediaFormats
 * or undefined if no image is available.
 */
export function getSourceImage(
    source: HydratedCuratedItem,
    format: MediaFormat
): HydratedImage | undefined {
    const { media } = source || {};

    if (!media || !isImageMediaFormat(format)) return undefined;

    switch (format) {
        case MediaFormat.audioImage:
            return media.audioImage;
        case MediaFormat.magazineCoverImage:
            return media.magazineCoverImage;
        case MediaFormat.newsletterImage:
            return media.newsletterImage;
        case MediaFormat.videoImage:
            return media.videoImage?.image;
        case MediaFormat.authorImage:
            return media.authorImage;
        case MediaFormat.defaultImage:
            return media.defaultImage;
        default:
            return undefined;
    }
}

interface AuthorImageProps {
    authorImage?: HydratedAuthorImageData;
    config?: MediaConfig;
}

export function AuthorImageMedia({
    authorImage,
    config = {},
}: AuthorImageProps) {
    const {
        mobileMaxWidth = 75,
        desktopMaxWidth = 150,
        aspectRatio = 1,
        className,
    } = useMemo(() => ({ ...config }), [config]);

    if (!authorImage) return null;

    return (
        <div
            data-component="media--authorImage"
            className={createClasses([
                styles.authorImage,
                className && className,
            ])}
        >
            <LazyRetinaImage
                image={authorImage}
                mobileMaxWidth={mobileMaxWidth}
                desktopMaxWidth={desktopMaxWidth}
                aspectRatio={aspectRatio}
            />
        </div>
    );
}

export function ImageMedia({ source, format, config = {} }: MediaChildProps) {
    const sourceImage = getSourceImage(source, format);

    const {
        aspectRatio,
        altText,
        desktopMaxWidth,
        mobileMaxWidth,
        showCaption = true,
        height,
        width,
        useImageLink = true,
        showPlayIcon = false,
        hideVideoDuration = false,
    } = useMemo(() => ({ ...config }), [config]);

    if (!sourceImage || !source?.media) return null;

    const componentID = `media--${format}`;

    const altTextFallback = altText || source.headline;

    if (format === MediaFormat.authorImage && source.media.authorImage) {
        return (
            <AuthorImageMedia
                authorImage={source.media.authorImage}
                config={config}
            />
        );
    }

    const useVideoLinkImage =
        format === MediaFormat.videoImage && !showPlayIcon;
    const contentType = showPlayIcon ? "relatedWithPlayIcon" : source.type;

    // This component is used with these props
    // for all image MediaFormat[s], it can be returned as-is
    // or wrapped in a container or even packaged
    // with siblings, like a play icon.
    const LazyRetinaImageWithProps = () => (
        <LazyRetinaImage
            aspectRatio={aspectRatio}
            image={sourceImage}
            altFallback={altTextFallback}
            contentType={contentType}
            height={height}
            width={width}
            desktopMaxWidth={desktopMaxWidth}
            mobileMaxWidth={mobileMaxWidth}
            showCaption={showCaption}
        />
    );

    // the video link image adds an SVG component
    // and div for styling.
    const VideoLinkImage = () => {
        const duration = !hideVideoDuration
            ? source.media?.videoImage?.videoDuration
            : undefined;

        return (
            <>
                <PlayIconWithDuration duration={duration} />
                <LazyRetinaImageWithProps />
                <div className={styles.gradient} />
            </>
        );
    };

    // className from MediaConfig is added to the
    // container element here
    const ImageContainer = () => {
        return (
            <section
                className={createClasses([
                    styles.image,
                    styles[format],
                    config.className && config.className,
                ])}
                data-component={componentID}
            >
                {useVideoLinkImage ? (
                    <VideoLinkImage />
                ) : (
                    <LazyRetinaImageWithProps />
                )}
            </section>
        );
    };

    // this prop links the headline
    // to the source url, and adds
    // a hover effect which highlights
    // the story's headline in a sibling <div>
    if (useImageLink) {
        return (
            <a
                href={source?.url}
                className={createClasses([
                    styles.imageOverlayLink,
                    styles.linkedImage,
                ])}
                data-component="image-media-link"
            >
                <ImageContainer />
            </a>
        );
    }

    return <ImageContainer />;
}
